import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subscription } from 'rxjs'
import { NavigationStart, Router } from '@angular/router'
import { filter, map } from 'rxjs/operators'
import { Auth as auth } from '@aws-amplify/auth/lib-esm/Auth'
import { APIService, GetUserQuery } from '../API.service'
import {
  mapGroupNameToUserType,
  UserGroup
} from '../shared/types/user-group.types'

@Injectable({
  providedIn: 'root'
})
export class SessionService {
  private loadAuthSub: Subscription

  // Keep a set of observable properties of the session that watches properties
  jwtToken$: BehaviorSubject<string>
  email$: BehaviorSubject<string>
  phoneNumber$: BehaviorSubject<string>
  userType$: BehaviorSubject<string>
  accessGroups$: BehaviorSubject<Array<UserGroup>>
  loggedIn$: BehaviorSubject<boolean>
  loadedSession$: BehaviorSubject<boolean>
  currentRoute$: BehaviorSubject<string>
  currentPageName$: Observable<string>
  userId$: BehaviorSubject<string>
  currentUser$: BehaviorSubject<GetUserQuery>

  constructor(private router: Router, private apiService: APIService) {
    this.currentRoute$ = new BehaviorSubject<string>(null)
    this.jwtToken$ = new BehaviorSubject<string>(null)
    this.email$ = new BehaviorSubject<string>(null)
    this.phoneNumber$ = new BehaviorSubject<string>(null)
    // @ts-ignore
    this.accessGroups$ = new BehaviorSubject<Array<UserGroup>>([])
    this.userType$ = new BehaviorSubject<string>(null)
    this.loggedIn$ = new BehaviorSubject<boolean>(false)
    this.loadedSession$ = new BehaviorSubject<boolean>(false)
    this.userId$ = new BehaviorSubject<string>(null)
    this.currentUser$ = new BehaviorSubject<GetUserQuery>(null)

    this.loadSession()
  }

  ngOnDestroy(): void {
    this.loadAuthSub.unsubscribe()

    this.jwtToken$.unsubscribe()
    this.email$.unsubscribe()
    this.phoneNumber$.unsubscribe()
    this.accessGroups$.unsubscribe()
    this.userType$.unsubscribe()
    this.loggedIn$.unsubscribe()
    this.loadedSession$.unsubscribe()
    this.currentRoute$.unsubscribe()
    this.userId$.unsubscribe()
    this.currentUser$.unsubscribe()
  }

  async ngOnInit(): Promise<void> {
    await this.loadSession()

    this.loadAuthSub = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(async event => {
        await this.loadSession()
      })
  }

  /***
   * Loads the auth session onto observables
   */
  async loadSession(): Promise<any> {
    try {
      const session = await auth.currentSession()
      const info = await auth.currentUserInfo()
      const jwtToken = session.getAccessToken().getJwtToken()
      const idPayload = session.getIdToken().decodePayload()
      const email = idPayload['email']
      const phoneNumber = idPayload['phone_number']
      const id = idPayload['sub']
      console.log(id)
      const accessGroups =
        session.getAccessToken().decodePayload()['cognito:groups'] || []
      const userType = this.asUserType(accessGroups[0] || null)
      const loggedIn = session.isValid() && !!info
      console.log('Loading Session', {
        loggedIn,
        accessGroups,
        info,
        session,
        jwtToken,
        email,
        phoneNumber,
        userType
      })
      this.jwtToken$.next(jwtToken)
      this.email$.next(email)
      this.phoneNumber$.next(phoneNumber)
      this.accessGroups$.next(accessGroups)
      this.userType$.next(userType)
      this.loggedIn$.next(loggedIn)
      this.userId$.next(id)
      const user = await this.apiService.GetUser(id)
      console.log('User is', user)
      this.currentUser$.next(user)
    } catch (err) {
      // When there's an error, it is probably caused by no auth user
      console.log('Failed at Loading Session', err)
      this.jwtToken$.next(null)
      this.email$.next(null)
      this.phoneNumber$.next(null)
      // @ts-ignore
      this.accessGroups$.next([])
      this.loggedIn$.next(false)
      this.userId$.next(null)
      this.currentUser$.next(null)
    } finally {
      this.loadedSession$.next(true)
    }
  }

  // Convert group name to a userType format
  asUserType(groupName: string): string {
    const userType = mapGroupNameToUserType(groupName)
    if (!userType) {
      // UNSUPPORTED USER TYPE
      alert('Unsupported User Type. Please Contact Administrator.')
      auth.signOut().then(() => {
        this.router.navigate(['login'])
      })
    }
    return userType
  }
}
